import { deleteCookie, getCookie, setCookie } from 'cookies-next';

/**
 * Unified cookie service for authentication
 * Uses buyer_ prefix for all cookies to maintain compatibility
 */

const COOKIE_KEYS = {
  JWT_TOKEN: 'buyer_jwt_token',
  REFRESH_TOKEN: 'buyer_refresh_token',
  ACTIVE_ROLE: 'buyer_active_role',
  SELECTED_SHIPTO_ID: 'buyer_selected_shipto_id',
  SELECTED_SHIPTO_DC: 'buyer_selected_shipto_dc',
} as const;

const PROD_BUYER_HOSTNAME = 'buyer.sukandaonelink.com';
const SHARED_AUTH_COOKIE_DOMAIN = '.sukandaonelink.com';

type AuthCookieOptions = {
  path: string;
  sameSite: 'lax';
  secure: boolean;
  domain?: string;
};

function isHttpsClient(): boolean {
  return typeof window !== 'undefined' && window.location.protocol === 'https:';
}

function getSharedAuthCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  if (window.location.hostname !== PROD_BUYER_HOSTNAME) return undefined;
  return SHARED_AUTH_COOKIE_DOMAIN;
}

function getAuthCookieSetOptions(): AuthCookieOptions {
  const options: AuthCookieOptions = {
    path: '/',
    sameSite: 'lax',
    secure: isHttpsClient(),
  };
  const domain = getSharedAuthCookieDomain();
  if (domain) {
    options.domain = domain;
  }
  return options;
}

function getAuthCookieDeleteOptionSets(): AuthCookieOptions[] {
  const hostOnly: AuthCookieOptions = {
    path: '/',
    sameSite: 'lax',
    secure: isHttpsClient(),
  };
  const domain = getSharedAuthCookieDomain();
  if (!domain) return [hostOnly];
  return [hostOnly, { ...hostOnly, domain }];
}

function deleteAuthCookie(name: string): void {
  for (const options of getAuthCookieDeleteOptionSets()) {
    deleteCookie(name, options);
  }
}

/**
 * Unified cookie service for authentication
 */
export class CookieService {
  /**
   * Set authentication tokens in cookies
   */
  static setAuthTokens(token: string, refreshToken: string): void {
    const options = getAuthCookieSetOptions();
    setCookie(COOKIE_KEYS.JWT_TOKEN, token, options);
    setCookie(COOKIE_KEYS.REFRESH_TOKEN, refreshToken, options);
  }

  /**
   * Set user session data in cookies
   */
  static setSessionData(role: string, shipToId = '', shipToDc = ''): void {
    const options = getAuthCookieSetOptions();
    setCookie(COOKIE_KEYS.ACTIVE_ROLE, role, options);

    if (shipToId) {
      setCookie(COOKIE_KEYS.SELECTED_SHIPTO_ID, shipToId, options);
    }
    if (shipToDc) {
      setCookie(COOKIE_KEYS.SELECTED_SHIPTO_DC, shipToDc, options);
    }
  }

  /**
   * Set complete authentication data (tokens + session)
   */
  static setAuthData(
    token: string,
    refreshToken: string,
    role: string,
    shipToId = '',
    shipToDc = ''
  ): void {
    this.setAuthTokens(token, refreshToken);
    this.setSessionData(role, shipToId, shipToDc);
  }

  /**
   * Get JWT token from cookies
   */
  static getJwtToken(): string | undefined {
    const token = getCookie(COOKIE_KEYS.JWT_TOKEN);
    return typeof token === 'string' ? token : undefined;
  }

  /**
   * Get refresh token from cookies
   */
  static getRefreshToken(): string | undefined {
    const token = getCookie(COOKIE_KEYS.REFRESH_TOKEN);
    return typeof token === 'string' ? token : undefined;
  }

  /**
   * Get active role from cookies
   */
  static getActiveRole(): string | undefined {
    const role = getCookie(COOKIE_KEYS.ACTIVE_ROLE);
    return typeof role === 'string' ? role : undefined;
  }

  /**
   * Get selected ship-to ID from cookies (buyer only)
   */
  static getSelectedShipToId(): string | undefined {
    const shipToId = getCookie(COOKIE_KEYS.SELECTED_SHIPTO_ID);
    return typeof shipToId === 'string' ? shipToId : undefined;
  }

  /**
   * Get selected ship-to DC from cookies (buyer only)
   */
  static getSelectedShipToDc(): string | undefined {
    const shipToDc = getCookie(COOKIE_KEYS.SELECTED_SHIPTO_DC);
    return typeof shipToDc === 'string' ? shipToDc : undefined;
  }

  /**
   * Clear all authentication-related cookies
   */
  static clearAllAuthCookies(): void {
    deleteAuthCookie(COOKIE_KEYS.JWT_TOKEN);
    deleteAuthCookie(COOKIE_KEYS.REFRESH_TOKEN);
    deleteAuthCookie(COOKIE_KEYS.ACTIVE_ROLE);
    deleteAuthCookie(COOKIE_KEYS.SELECTED_SHIPTO_ID);
    deleteAuthCookie(COOKIE_KEYS.SELECTED_SHIPTO_DC);
  }

  /**
   * Clear only authentication tokens (keep session data)
   */
  static clearAuthTokens(): void {
    deleteAuthCookie(COOKIE_KEYS.JWT_TOKEN);
    deleteAuthCookie(COOKIE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Check if user is authenticated (has valid tokens)
   */
  static isAuthenticated(): boolean {
    return !!(this.getJwtToken() && this.getRefreshToken());
  }

  /**
   * Get all authentication data from cookies
   */
  static getAllAuthData() {
    return {
      jwtToken: this.getJwtToken(),
      refreshToken: this.getRefreshToken(),
      activeRole: this.getActiveRole(),
      selectedShipToId: this.getSelectedShipToId(),
      selectedShipToDc: this.getSelectedShipToDc(),
    };
  }
}

export const BuyerCookieService = CookieService;
export const AdminCookieService = CookieService;
